/**
 * The knowledge base's record rules (piece 30): the statuses, the three
 * visibility values, the author tier ladder, the question types, the tag
 * targets, the slug, the Settings rows it reads and the job it runs.
 */
import type { StatusDefinitionRow } from '../statusDefinitions';
import { t } from '../strings';

/** 🔨 Draft, Published or Archived, and an archived article is never deleted (piece 30, The Article record). */
export const ARTICLE_STATUSES = ['Draft', 'Published', 'Archived'] as const;
export type ArticleStatusValue = (typeof ARTICLE_STATUSES)[number];

/** Exactly three values; there is no fourth, location-restricted tier (piece 30, Categories and visibility). */
export const ARTICLE_VISIBILITIES = ['Public', 'Internal', 'Group'] as const;
export type ArticleVisibilityValue = (typeof ARTICLE_VISIBILITIES)[number];

/**
 * 🔨 The author tiers, a ladder in this order: a tier holds every tier before
 * it (decision 0751). Update and Delete reach the viewer's own articles, and
 * UpdateDeleteAll everybody's.
 */
export const ARTICLE_TIERS = ['View', 'Create', 'Update', 'Delete', 'UpdateDeleteAll', 'ManageCategories', 'AdminAccess'] as const;
export type ArticleTier = (typeof ARTICLE_TIERS)[number];

/** Single-choice, multiple-choice and true/false; free text is deliberately absent. */
export const QUIZ_QUESTION_TYPES = ['SingleChoice', 'MultipleChoice', 'TrueFalse'] as const;
export type QuizQuestionTypeValue = (typeof QUIZ_QUESTION_TYPES)[number];

/** 🔨 An article's tags reach two registered targets (decision 0183). */
export const ARTICLE_TAG_TARGETS = ['AssetNode', 'Item'] as const;
export type ArticleTagTarget = (typeof ARTICLE_TAG_TARGETS)[number];

export function isArticleTagTarget(value: string): value is ArticleTagTarget {
  return (ARTICLE_TAG_TARGETS as readonly string[]).includes(value);
}

/** The seeded rows the status pill reads (decision 0639); the migration inserts the same three. */
export const ARTICLE_STATUS_DEFINITIONS: readonly StatusDefinitionRow[] = [
  { entityType: 'ArticleStatus', statusValue: 'Draft', label: 'Draft', colorGroup: 'warning', sortOrder: 1 },
  { entityType: 'ArticleStatus', statusValue: 'Published', label: 'Published', colorGroup: 'good', sortOrder: 2 },
  { entityType: 'ArticleStatus', statusValue: 'Archived', label: 'Archived', colorGroup: 'inactive', sortOrder: 3 },
];

/** The article's route is `/kb/:id`; the slug is for readable links only. */
export const KB_ROUTE = '/kb';
/** 🔒 Decision 2281: the Knowledge section's Checklists tab, a literal segment registered before `/kb/:id`. */
export const KB_CHECKLISTS_ROUTE = `${KB_ROUTE}/checklists`;
/** 🔒 Decision 2281: the anchor of one template's card on the Checklists tab, which the article's Used in Checklists panel opens. */
export function checklistTemplateAnchor(templateId: string): string {
  return `template-${templateId}`;
}
export function articleRoute(articleId: string): string {
  return `${KB_ROUTE}/${articleId}`;
}

/** 🔒 Decisions 12580 and 12581: the two words of the article page's address that open it in its working mode for one job's checklist. */
export const WORKING_WORK_ORDER_PARAM = 'workOrder';
export const WORKING_CHECKLIST_PARAM = 'checklist';

/** 🔒 Decision 12580: where Open Work Instruction goes, the article page in its working mode for that job's checklist (decision 12581). */
export function workInstructionRoute(articleId: string, workOrderId: string, checklistId: string): string {
  const query = new URLSearchParams({ [WORKING_WORK_ORDER_PARAM]: workOrderId, [WORKING_CHECKLIST_PARAM]: checklistId });
  return `${articleRoute(articleId)}?${query.toString()}`;
}

/** 🔒 Decision 11541 (T3687): an article's running number reads `KB-` and the number padded to four digits, as KB-0457. */
export const ARTICLE_NUMBER_PREFIX = t('kb.number.prefix');
export const ARTICLE_NUMBER_PADDING = 4;

/** The number as a person reads it; a number past 9999 keeps every digit. */
export function articleNumberLabel(articleNumber: number): string {
  return `${ARTICLE_NUMBER_PREFIX}${String(articleNumber).padStart(ARTICLE_NUMBER_PADDING, '0')}`;
}

/**
 * The article number a search term names, or null: `KB-0457`, `KB0457`, `kb 457` or a bare `457`, the whole term and
 * nothing beside it. 🔨 A term with any other word in it is a words search alone.
 */
export function articleNumberOf(term: string): number | null {
  const match = /^\s*(?:kb[\s-]*)?0*(\d{1,9})\s*$/i.exec(term);
  if (!match) return null;
  const value = Number(match[1]);
  return value > 0 ? value : null;
}

/** A person's right and wrong answers by quiz and by category (decision 0190). */
export function personReportRoute(personId: string): string {
  return `${KB_ROUTE}/people/${personId}`;
}

/** The address a failure-rate to-do opens: the question's own article, found from the question. */
export function quizQuestionRoute(questionId: string): string {
  return `${KB_ROUTE}/questions/${questionId}`;
}

export const SLUG_MAX_LENGTH = 80;

/** A unique, lower-cased form of the title; the service appends a counter where it would collide. */
export function slugFor(title: string): string {
  const slug = title
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX_LENGTH)
    .replace(/-+$/g, '');
  return slug || 'article';
}

/** The slug with a counter, for the second article with the same title. */
export function slugWithCounter(slug: string, counter: number): string {
  const suffix = `-${counter}`;
  return `${slug.slice(0, SLUG_MAX_LENGTH - suffix.length).replace(/-+$/g, '')}${suffix}`;
}

export interface AudienceShape {
  visibility: ArticleVisibilityValue;
  visibilitySystemRoleId: string | null;
  visibilityDepartmentId: string | null;
}

/** The audience columns read off an article or a category row. */
export function audienceOf(row: AudienceShape): AudienceShape {
  return { visibility: row.visibility, visibilitySystemRoleId: row.visibilitySystemRoleId, visibilityDepartmentId: row.visibilityDepartmentId };
}

/**
 * 🔨 A Group article or category names exactly one audience, a role or a
 * department, and is refused on save with neither; a Public or Internal one
 * names none (piece 30, The Article record).
 */
export function audienceProblem(audience: AudienceShape): string | null {
  if (!(ARTICLE_VISIBILITIES as readonly string[]).includes(audience.visibility)) return `${String(audience.visibility)} is not a visibility; it is Public, Internal or Group`;
  const role = Boolean(audience.visibilitySystemRoleId);
  const department = Boolean(audience.visibilityDepartmentId);
  if (audience.visibility === 'Group') {
    if (!role && !department) return 'a Group names the role or the department it is readable by';
    if (role && department) return 'a Group names a role or a department, not both';
    return null;
  }
  if (role || department) return `a ${audience.visibility} visibility names no group; choose Group to narrow it`;
  return null;
}

/**
 * The two Settings rows the failure flag reads (piece 30, Quiz analytics), both
 * at Organization scope and seeded as the open issue proposes: 50 percent over
 * 10 first attempts.
 */
export const KB_FAILURE_THRESHOLD_KEY = 'kb.questionFailureThresholdPercent';
export const KB_FAILURE_THRESHOLD_DEFAULT = 50;
export const KB_FAILURE_MINIMUM_KEY = 'kb.questionFailureMinimumAttempts';
export const KB_FAILURE_MINIMUM_DEFAULT = 10;

/**
 * 🔒 The nightly move of a lapsed completion to Expired (decision 0754), on the
 * job clock under its own hour key, seeded at 2.
 */
export const EXPIRE_COMPLETIONS_JOB = 'expireRequirementCompletions';
export const EXPIRE_COMPLETIONS_HOUR_KEY = 'kb.expireCompletionsRunHour';
export const EXPIRE_COMPLETIONS_DEFAULT_HOUR = 2;

/** The Settings rows piece 30 seeds, each once and never overwritten. */
export const PIECE_30_SETTINGS: readonly { key: string; value: number }[] = [
  { key: KB_FAILURE_THRESHOLD_KEY, value: KB_FAILURE_THRESHOLD_DEFAULT },
  { key: KB_FAILURE_MINIMUM_KEY, value: KB_FAILURE_MINIMUM_DEFAULT },
];

/** A percentage the failure flag reads, refused outside one to a hundred. */
export function thresholdProblem(percent: unknown): string | null {
  return typeof percent === 'number' && Number.isInteger(percent) && percent >= 1 && percent <= 100 ? null : 'the failure threshold is a whole percentage from 1 to 100';
}

/** The sample the failure flag waits for, refused below one. */
export function minimumAttemptsProblem(count: unknown): string | null {
  return typeof count === 'number' && Number.isInteger(count) && count >= 1 && count <= 10_000 ? null : 'the minimum sample is a whole number of first attempts, at least 1';
}

/**
 * 🔒 The W-9 review requirement type is one seeded row with a stable id
 * (decision 0753). The W-9 approval gate reads it by that id and never by its
 * name, so renaming the row in Settings changes nothing the gate answers.
 */
export const W9_REVIEW_REQUIREMENT_TYPE_ID = '0192a5c0-0000-7000-8000-000000000930';
export const W9_REVIEW_REQUIREMENT_TYPE_NAME = 'W-9 Verification Training';

/** The generated to-do a question's failure rate raises on the article's author (decision 0056). */
export const QUIZ_FAILURE_KIND = 'QuizQuestionFailureRate' as const;
export const QUIZ_QUESTION_SOURCE = 'QuizQuestion' as const;
